import { CompetitionTypeSelect } from '~/pages/competition/components/competition-type-select';
import { DisciplineSelect } from '~/pages/discipline/components/discipline-select';
import { WeightClassSelect } from '~/pages/weight/components/weight-class-select';
import { DistrictMultiSelect } from '~/pages/district/components/district-multi-select';
import { CategorySelect } from '~/pages/categories/components/category-select';
import { ClubMultiSelect } from '~/pages/club/components/club-multi-select';
import { SexFilterSelect } from '~/pages/sex/components/sex-filter-select';
import { paginationSchema } from '~/components/Pagination/Pagination.schema';
import { useDebouncedCallback } from '~/hooks/useDebouncedCallback';
import { useFilterForm } from '~/hooks/useFilterForm';
import { QuerySchema, querySchema } from '../query-schema';
import { Table } from '~/components/Table/Table';
import { Button } from '~/components/Button/Button';
import { Grid } from '~/components/Grid/Grid';
import { toIsMaleFilter } from '~/utils/sex';
import { Input } from '~/components/Input';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import _ from 'lodash';

const SEARCH_DEBOUNCE_MS = 300;

/** URL keys holding filter state, i.e. everything except pagination. */
const FILTER_KEYS = Object.keys(querySchema.shape).filter(
  key => !(key in paginationSchema.shape)
) as (keyof QuerySchema)[];

/**
 * Picks the filter values, dropping empty ones so that an explicitly empty param
 * (e.g. "Alla kategorier" or a cleared date) compares equal to an absent one.
 */
function pickFilters(values: QuerySchema) {
  return _.omitBy(_.pick(values, FILTER_KEYS), value => value === undefined || value === '');
}

const DEFAULT_FILTERS = pickFilters(querySchema.parse({}));

export function RankingListFilter() {
  const { setValues, setValue, values } = useFilterForm(querySchema.parse);

  const handleSearchChange = useDebouncedCallback(
    (search: string) => setValue('search', search || undefined),
    SEARCH_DEBOUNCE_MS
  );

  function handleCompetitionTypeChange(event: ChangeEvent<HTMLSelectElement>) {
    setValues({
      competition_type_id: event.target.value,
      age_category_id: undefined,
      weight_class_id: undefined,
    });
  }

  function handleAgeCategoryChange(event: ChangeEvent<HTMLSelectElement>) {
    setValues({ age_category_id: event.target.value, weight_class_id: undefined });
  }

  function toIdList(ids: number[]) {
    return ids.length ? ids.join(',') : undefined;
  }

  function handleSexChange(event: ChangeEvent<HTMLSelectElement>) {
    setValues({ sex: event.target.value, weight_class_id: undefined });
  }

  const advancedFilterCount = [
    values.districts.length > 0,
    values.clubs.length > 0,
    values.current_age_categories,
  ].filter(Boolean).length;

  const hasActiveFilters = !_.isEqual(pickFilters(values), DEFAULT_FILTERS);

  // The search and date inputs are uncontrolled (defaultValue), so clearing the URL alone would not
  // update what they display; bumping this key remounts them. The router applies the URL change
  // asynchronously, so the remount waits for it, otherwise the inputs would mount with stale values.
  const { search: locationSearch } = useLocation();
  const [resetKey, setResetKey] = useState(0);
  const isResetPending = useRef(false);

  useEffect(() => {
    if (isResetPending.current) {
      isResetPending.current = false;
      setResetKey(key => key + 1);
    }
  }, [locationSearch]);

  function handleReset() {
    handleSearchChange.cancel();
    isResetPending.current = true;
    setValues(Object.fromEntries(FILTER_KEYS.map(key => [key, undefined])));
  }

  return (
    <div
      style={{
        borderBottom: '2px solid var(--color-fg-secondary)',
        paddingBottom: 'var(--size-4)',
        marginBottom: 'var(--size-4)',
      }}
    >
      <Grid key={resetKey}>
        <Grid.Item cols={{ sm: 12, md: 4 }}>
          <Table.Search
            onChange={event => handleSearchChange(event.target.value)}
            placeholder="Sök på namn, förening eller distrikt..."
            defaultValue={values.search}
            aria-label="Sök"
          />
        </Grid.Item>

        <Grid.Item cols={{ sm: 6, md: 3 }}>
          <CompetitionTypeSelect
            defaultValue={values.competition_type_id}
            onChange={handleCompetitionTypeChange}
            placeholder={undefined}
          />
        </Grid.Item>

        <Grid.Item cols={{ sm: 6, md: 3 }}>
          <CategorySelect
            query={{ competition_types: values.competition_type_id ? [values.competition_type_id] : undefined }}
            defaultValue={values.age_category_id}
            onChange={handleAgeCategoryChange}
          />
        </Grid.Item>

        <Grid.Item cols={{ sm: 6, md: 2 }}>
          <SexFilterSelect onChange={event => handleSexChange(event)} defaultValue={values.sex} />
        </Grid.Item>

        <Grid.Item cols={{ sm: 6, md: 2 }}>
          <WeightClassSelect
            query={{
              competition_types: values.competition_type_id ? [values.competition_type_id] : undefined,
              age_category: values.age_category_id ?? undefined,
              is_male: toIsMaleFilter(values.sex),
            }}
            onChange={event => setValue('weight_class_id', event.target.value)}
            key={`${values.sex}-${values.weight_class_id}`}
            defaultValue={values.weight_class_id}
            name="weight_class_id"
          />
        </Grid.Item>

        <Grid.Item cols={{ sm: 6, md: 2 }}>
          <DisciplineSelect
            query={{ competition_type: values.competition_type_id ? values.competition_type_id : undefined }}
            onChange={event => setValue('discipline', event.target.value)}
            defaultValue={values.discipline}
            name="discipline"
          />
        </Grid.Item>

        <Grid.Item cols={{ sm: 6, md: 3 }}>
          <Input.Field
            onChange={event => setValue('from_date', event.target.value)}
            defaultValue={values.from_date}
            aria-label="Från datum"
            type="date"
          />
        </Grid.Item>

        <Grid.Item cols={{ sm: 6, md: 3 }}>
          <Input.Field
            onChange={event => setValue('to_date', event.target.value)}
            defaultValue={values.to_date}
            aria-label="Till datum"
            type="date"
          />
        </Grid.Item>

        <Grid.Item cols={{ sm: 6, md: 2 }}>
          <Table.Filter
            count={advancedFilterCount}
            footer={
              <Button variant="secondary" onClick={handleReset} disabled={!hasActiveFilters}>
                Rensa alla filter
              </Button>
            }
            block
          >
            <Table.FilterSection label="Distrikt">
              <DistrictMultiSelect
                onChange={ids => setValue('districts', toIdList(ids))}
                value={values.districts}
                placeholder="Alla distrikt"
              />
            </Table.FilterSection>

            <Table.FilterSection label="Förening">
              <ClubMultiSelect
                onChange={ids => setValue('clubs', toIdList(ids))}
                value={values.clubs}
                placeholder="Alla föreningar"
              />
            </Table.FilterSection>

            <Table.FilterSection label="Ålderskategori">
              <Input.InlineGroup
                htmlFor="current_age_categories"
                label="Filtrera utifrån lyftares nuvarande ålderskategori"
                neutral
              >
                <Input.Checkbox
                  onChange={event => setValue('current_age_categories', event.target.checked ? 'true' : undefined)}
                  checked={values.current_age_categories}
                  name="current_age_categories"
                />
              </Input.InlineGroup>
            </Table.FilterSection>
          </Table.Filter>
        </Grid.Item>
      </Grid>
    </div>
  );
}
